import {
  all, call, select, takeLatest, put
} from 'redux-saga/effects';
import { actions } from 'react/api/actions/chat';
import {
  parseMessage,
  parseUnreadMessagesToNewChats,
  triggerNewMessageAudio,
  encodeMessage
} from 'react/utils/chatUtils';
import defaultFetch from './fetch/defaultFetch';
import { getTicketOldMessages } from './chatTicketActions';

const {
  updateChatWindows,
  addMessage,
  sendMessage,
  updateSingleMessage,
  updateMessages,
  readMessage,
  readTicketMessages,
  receiveMessage,
  addNewChatWindow,
  getUnreadMessages,
  responseCall,
  toggleCallStatus
} = actions;

const logError = (err) => {
  new Monet.Exception
    .ReturnError(null, (err?.message || 'Error'), err)
    .console();
};

function* findChat(chatId) {
  const { chat } = yield select();

  return Object.values(chat.toJS()).find((item) => item.id === chatId);
}

function* findChatsByIDs(chatIds = []) {
  const { chat } = yield select();

  return Object.values(chat.toJS()).filter((item) => chatIds.includes(item.id));
}

function* handleResponseFromBroadcast(response) {

  const chatIDs = response.map(({ from: userId, to }) => `${userId}-${to}`);
  const activeChats = yield call(findChatsByIDs, chatIDs);

  const toUserIDs = response.map((item) => item.to);
  const usersTo = Monet.Session?.Chat?.Users?.filter(
    (user) => toUserIDs.includes(user.id)
  );

  const addMessagesToChatsActions = response.map(item => {
    const { from: userId, to, message } = item;

    const chatId = `${userId}-${to}`;
    const parsedMessage = parseMessage({ ...item, message }, 'internal');
    const hasActiveChat = activeChats.find((activeChat) => activeChat.id === chatId);

    if(hasActiveChat) {
      return put({
        type: addMessage,
        payload: {
          chatId,
          message: parsedMessage
        }
      });
    }

    const toUser = usersTo.find((user) => user.id === to);
    const payload = {
      from: toUser.id,
      fromName: toUser.name
    };

    return put({
      type: addNewChatWindow,
      payload: {
        ...payload,
        chatId,
        messages: [parsedMessage]
      }
    });
  });

  yield all(addMessagesToChatsActions);
}

function* handleResponseFromUniqueMessage(response, ticketId) {
  const { from: userId, to, message} = response;
  const chatId = ticketId || `${userId}-${to}`;
  const parsedMessage = parseMessage({ ...response, message }, 'internal');

  yield put({
    type: addMessage,
    payload: {
      chatId,
      message: parsedMessage
    }
  });
}

/** Send message */
function* sendMessageSaga(action) {
  const { message, to, ticketId } = action.payload;
  const encodedMessage = encodeMessage(message);

  const {
    user,
    configuration: { globalConfiguration: { authConfigId: companyId } }
  } = yield select();

  const userId = user.get('id');
  const userName = user.get('name');

  const objectToSend = {
    companyId,
    ticketId,
    from: userId,
    to,
    fromName: userName,
    message: encodedMessage,
    type: 'text'
  };

  const response = yield Monet.Session.Chat.Service.sendChatMessage(objectToSend, !!ticketId);

  if (response?.length && to.length > 1) {
    yield handleResponseFromBroadcast(response);
  } else if (response?.id && response?.sentDate) {
    yield handleResponseFromUniqueMessage(response, ticketId);
  }
}

function* watchSendMessage() {
  yield takeLatest(sendMessage, sendMessageSaga);
}

/** Receive message */
function* processMessage(messageData) {
  const parsedMessage = parseMessage(messageData, 'external');
  const { ticketId, from, id } = parsedMessage;

  const { user } = yield select();
  const userId = user.get('id');

  const chatId = ticketId || `${userId}-${from}`;

  yield put({
    type: addMessage,
    payload: {
      chatId,
      message: parsedMessage
    }
  });

  Monet.Session.Chat.Service.markMessageAsReceived({
    messageId: id,
    messagesIds: [id],
    isFromTicket: !!ticketId
  });
}

function* getTicketData(ticketId) {
  if (!ticketId) {
    return {};
  }

  const { user } = yield select();
  const userId = user.get('id');

  const criteria = [
    '*',
    'ticketType.id',
    'userAssigned.id',
    'userAssigned.name',
    'userCreated.name',
    'ticketLinked.route.key',
    'ticketLinked.location.description'
  ];
  const url = `/Ticket/restrictions?criteria={"filters":${JSON.stringify(criteria)}}`;
  const encodeUrl = encodeURI(url);
  const body = {
    criteriaChain: [
      {
        and: [
          {
            attr: 'id',
            eq: ticketId
          },
          {
            or: [
              {
                attr: 'ticketStatus',
                eq: 'ASSIGNED'
              },
              {
                attr: 'ticketStatus',
                eq: 'RE_ASSIGNED'
              }
            ]
          },
          {
            attr: 'userAssigned.id',
            eq: userId
          }
        ]
      }
    ]
  };

  const [response] = yield call(defaultFetch, encodeUrl, body, 'POST');

  if (response) {
    const {
      title,
      ticketStatus,
      userCreated: {
        id: userCreatedId,
        name: userName
      },
      ticketType: {
        id: ticketTypeId
      },
      ticketLinked: {
        route,
        location
      },
      created
    } = response;

    const oldMessagesOfTheTicket = yield getTicketOldMessages(ticketId);

    const parsedTicket = {
      ticketId,
      ticketTypeId,
      status: ticketStatus,
      routeKey: route.key,
      stopDescription: location.description,
      createdDate: created,
      fromTicketHub: true
    };

    return {
      name: title,
      fromName: userName,
      from: userCreatedId,
      ticket: parsedTicket,
      messages: oldMessagesOfTheTicket
    };
  }

  return {};
}

function* receiveMessageSaga(action) {
  const { from, ticketId } = action.payload;
  const { user } = yield select();
  const userId = user.get('id');

  const chatId = ticketId || `${userId}-${from}`;

  const hasActiveChat = yield call(findChat, chatId);

  if (!hasActiveChat) {
    const ticketData = ticketId ? yield getTicketData(ticketId) : {};
    Object.assign(action.payload, ticketData);

    yield put({ type: addNewChatWindow, payload: action.payload });
  }

  if (!hasActiveChat && ticketId) {
    return;
  }

  yield processMessage(action.payload);
}

function* watchReceiveMessage() {
  yield takeLatest(receiveMessage, receiveMessageSaga);
}

/** Read message */
function* readMessageSaga(action) {
  const { chatId, messageId } = action.payload;

  yield put({
    type: updateSingleMessage,
    payload: {
      chatId,
      message: {
        id: messageId,
        readDate: true
      }
    }
  });

  Monet.Session.Chat.Service.markChatMessageAsRead(messageId);
}

function* watchReadMessage() {
  yield takeLatest(readMessage, readMessageSaga);
}

/** Read ticket message */
function* readTicketMessagesSaga(action) {
  const { chatId, messages } = action.payload;
  const messagesIds = messages.map((item) => item.get('id')).toJS();

  yield put({
    type: updateMessages,
    payload: {
      chatId,
      messagesIds,
      dataToUpdate: {
        readDate: true
      }
    }
  });

  Monet.Session.Chat.Service.markTicketMessageAsRead(messagesIds);
}

function* watchTicketReadMessages() {
  yield takeLatest(readTicketMessages, readTicketMessagesSaga);
}

/** Get unread messages */
function* getUnreadMessagesSaga() {
  try {
    const messages = yield Monet.Session.Chat.Service.getUnreadMessages();
    const newChats = yield parseUnreadMessagesToNewChats(messages);

    if (Object.keys(newChats).length > 0) {
      triggerNewMessageAudio();

      yield put({
        type: updateChatWindows,
        payload: newChats
      });
    }
  } catch (error) {
    logError(error);
  }
}

function* watchGetUnreadMessages() {
  yield takeLatest(getUnreadMessages, getUnreadMessagesSaga);
}

/** Response a call */
function* responseCallSaga(action) {
  const { accepted, chatId, userId } = action.payload;

  if (accepted) {
    yield Monet.Session.Chat.Service.answerCall(userId);
  }

  yield put({
    type: toggleCallStatus,
    payload: {
      chatId
    }
  });
}

function* watchResponseCall() {
  yield takeLatest(responseCall, responseCallSaga);
}

export default function* () {
  yield all([
    watchSendMessage(),
    watchReceiveMessage(),
    watchReadMessage(),
    watchTicketReadMessages(),
    watchGetUnreadMessages(),
    watchResponseCall()
  ]);
}
